import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyField } from "./CopyField";
import { Icon } from "./icons";
import { Alert, Button, Card, CardHeader, ConfirmPrompt, Field, Input } from "./ui";
import { ApiError } from "../lib/api";
import { relativeTime } from "../lib/format";
import { regionLabel } from "../lib/regions";
import {
  useAccessRules,
  useAddAccessRule,
  useRemoveAccessRule,
  useRotateDatabasePassword,
  useSetExternalAccess,
} from "../lib/queries";
import type { Database } from "../lib/types";

/**
 * Two actions that change who can reach a database and with what credential.
 *
 * Both are admin-only and both are consequential, so neither is a bare switch.
 * Exposing needs a typed confirmation; rotating needs a second click and names
 * the applications it will break. Withdrawing exposure needs nothing — friction
 * belongs on the way out, not on the way back to safety.
 *
 * The connection card above these (in AccessTab) is the read side; these are
 * the change side. The password stays behind its reveal either way.
 */
export function DatabaseAccess({
  orgId,
  database,
  canManage,
}: {
  orgId: string | undefined;
  database: Database;
  canManage: boolean;
}) {
  const { t } = useTranslation("database");
  const exposed = database.externalPort !== null;
  const where = database.region ? t("exposure.inRegion", { region: regionLabel(database.region) }) : "";

  const setAccess = useSetExternalAccess(orgId ?? "");
  const rotate = useRotateDatabasePassword(orgId ?? "");
  /* Shares the allowlist card's query key, so this costs no second request —
     and skips the request entirely while the port is private, when the card
     it feeds is not rendered. The warning below must not keep shouting
     "anyone can reach this" over an allowlist the server confirmed enforcing. */
  const accessRules = useAccessRules(orgId, exposed ? database.id : undefined);
  const restricted =
    (accessRules.data?.data.length ?? 0) > 0 && accessRules.data?.enforcement === "applied";

  const [confirmText, setConfirmText] = useState("");
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rotated, setRotated] = useState<{
    connectionString: string | null;
    staleIn: { id: string; name: string }[];
  } | null>(null);

  // A rotate confirmation left alone reverts, so a stray click cannot sit there
  // waiting to be completed by the next one.
  useEffect(() => {
    if (!confirmingRotate) return;
    const timer = setTimeout(() => setConfirmingRotate(false), 4000);
    return () => clearTimeout(timer);
  }, [confirmingRotate]);

  async function open() {
    setError(null);
    try {
      await setAccess.mutateAsync({ databaseId: database.id, enabled: true, confirm: "expose" });
      setConfirmText("");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("exposure.openFailed"));
    }
  }

  async function close() {
    setError(null);
    try {
      await setAccess.mutateAsync({ databaseId: database.id, enabled: false });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("exposure.closeFailed"));
    }
  }

  async function rotatePassword() {
    if (!confirmingRotate) {
      setConfirmingRotate(true);
      return;
    }
    setConfirmingRotate(false);
    setError(null);
    setRotated(null);
    try {
      const result = await rotate.mutateAsync(database.id);
      setRotated({ connectionString: result.connectionString, staleIn: result.staleIn });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("password.failed"));
    }
  }

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}

      {/* The private path is the region's Docker network, so "your apps" means
          the ones placed beside this database; the rest need the port. */}
      <Card>
        <CardHeader
          title={t("exposure.title")}
          /*
           * Three states, not two. "Anyone who finds the address" over a row
           * saying the port is restricted to the allowed networks is the card
           * contradicting itself, and the reassuring half is the one that is
           * wrong -- it is the heading a reader takes away.
           */
          description={
            !exposed
              ? t("exposure.private", { where })
              : restricted
                ? t("exposure.exposedRestricted")
                : t("exposure.exposed")
          }
        />

        <div className="space-y-4 p-5">
          {exposed ? (
            <>
              {/*
                The address itself is on the Connection card above, with every
                other way of reaching this database. What belongs here is the
                decision: that it is open, since when, to whom, and the way
                back. Carrying the address in both places made a reader check
                two cards to be sure they matched.
              */}
              {!database.externalHost && (
                <Alert tone="warning">
                  Open on port {database.externalPort}, but no address was created. Close it and
                  open it again.
                </Alert>
              )}

              <div
                className={
                  restricted
                    ? "flex flex-wrap items-center gap-3 rounded-md border border-line bg-raised p-3"
                    : "flex flex-wrap items-center gap-3 rounded-md border border-warn/30 bg-warn/8 p-3"
                }
              >
                <Icon
                  name={restricted ? "check" : "warning"}
                  className={
                    restricted ? "size-4 shrink-0 text-mint" : "size-4 shrink-0 text-warn"
                  }
                />
                <p
                  className={
                    restricted
                      ? "min-w-0 flex-1 text-[13px] text-fg-muted"
                      : "min-w-0 flex-1 text-[13px] text-warn"
                  }
                >
                  {restricted
                    ? t("exposure.restricted", {
                        when: relativeTime(database.externalPortSetAt),
                      })
                    : t("exposure.open", { when: relativeTime(database.externalPortSetAt) })}
                </p>
                {canManage && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={setAccess.isPending}
                    onClick={() => void close()}
                  >
                    {t("exposure.close")}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px] text-fg-subtle">
                {t("exposure.privateNote", { where })}
              </p>

              {/*
                No port field: the platform picks the number, because a host port
                belongs to the machine and two tenants sharing one could
                otherwise ask for the same one. The confirmation is all that is
                left to type — `items-end` bottom-aligns each cell, so anything
                below the input would lift it off the button.
              */}
              {canManage && (
                <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <Field
                    label={<ConfirmPrompt phrase="expose" sentence={(slot) => t("exposure.typeToConfirm", { phrase: slot })} />}
                    htmlFor="external-confirm"
                  >
                    <Input
                      id="external-confirm"
                      autoComplete="off"
                      spellCheck={false}
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                    />
                  </Field>
                  <Button
                    variant="danger"
                    loading={setAccess.isPending}
                    disabled={confirmText !== "expose"}
                    onClick={() => void open()}
                  >
                    {t("exposure.openPort")}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/*
        The allowlist on the open port. Only rendered while a port is open —
        rules on a private database enforce nothing and would read as if they
        did. The enforcement line is the honest half of the feature: the rules
        live here, the firewall lives on the server, and claiming "restricted"
        while the agent has not confirmed anything is the one lie this panel
        must not tell.
      */}
      {exposed && (
        <AccessRulesCard orgId={orgId} databaseId={database.id} canManage={canManage} />
      )}

      <Card>
        <CardHeader title={t("password.title")} description={t("password.description")} />

        <div className="space-y-4 p-5">
          {rotated ? (
            <div className="space-y-3">
              <Alert tone="success">{t("password.changed")}</Alert>
              {rotated.connectionString && (
                <CopyField value={rotated.connectionString} label={t("password.connectionString")} />
              )}
              {rotated.staleIn.length > 0 && (
                <Alert tone="warning">
                  <span className="font-medium">
                    {rotated.staleIn.map((app) => app.name).join(", ")}
                  </span>{" "}
                  still have the old one. Attach the database to each again.
                </Alert>
              )}
            </div>
          ) : (
            <p className="text-[13px] text-fg-subtle">
              {t("password.warning")}
            </p>
          )}

          {canManage && (
            <Button
              variant={confirmingRotate ? "danger" : "secondary"}
              loading={rotate.isPending}
              onClick={() => void rotatePassword()}
            >
              {confirmingRotate ? t("password.confirm") : t("password.change")}
            </Button>
          )}
        </div>
      </Card>

      {!canManage && (
        <p className="text-xs text-fg-subtle">
          {t("password.needsAdmin")}
        </p>
      )}
    </div>
  );
}

/*
 * One short line per agent state, and a banner only when something is wrong.
 * The good path is a quiet mint dot — a full-width box saying "everything is
 * fine" is weight spent on the state that needs none.
 */
/* Tone per state, and the key naming its two sentences under `firewall`. */
const ENFORCEMENT = {
  applied: { dot: "text-mint", key: "applied" },
  pending: { dot: "text-warn", key: "pending" },
  unreachable: { dot: "text-danger", key: "unreachable", warns: true },
  unprovisioned: { dot: "text-warn", key: "unprovisioned", warns: true },
} as const;

function AccessRulesCard({
  orgId,
  databaseId,
  canManage,
}: {
  orgId: string | undefined;
  databaseId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation("database");
  const rules = useAccessRules(orgId, databaseId);
  const addRule = useAddAccessRule(orgId ?? "");
  const removeRule = useRemoveAccessRule(orgId ?? "");

  const [cidr, setCidr] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  /* Which row's Remove is in flight — one shared isPending would spin every
     row's button for one row's request. */
  const [removing, setRemoving] = useState<string | null>(null);

  const list = rules.data?.data ?? [];
  const enforcement = rules.data ? ENFORCEMENT[rules.data.enforcement] : null;

  async function add() {
    if (cidr.trim() === "" || addRule.isPending) return;
    setError(null);
    try {
      await addRule.mutateAsync({
        databaseId,
        cidr,
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setCidr("");
      setLabel("");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("allowlist.addFailed"));
    }
  }

  async function remove(ruleId: string) {
    setRemoving(ruleId);
    try {
      await removeRule.mutateAsync({ databaseId, ruleId });
    } finally {
      setRemoving(null);
    }
  }

  return (
    <Card>
      <CardHeader
        title={t("allowlist.title")}
        description={list.length === 0 ? t("allowlist.empty") : t("allowlist.description")}
      />

      <div className="space-y-4 p-5">
        {enforcement && (
          <div className="flex items-center gap-2.5 text-[13px] text-fg-muted">
            <span className={`dot-status ${enforcement.dot}`} aria-hidden="true" />
            {t(`firewall.${enforcement.key}` as "firewall.applied")}
          </div>
        )}
        {enforcement && "warns" in enforcement && (
          <Alert tone="warning">
            {t(`firewall.${enforcement.key}Warn` as "firewall.unreachableWarn")}
          </Alert>
        )}

        {list.length > 0 && (
          <ul className="divide-y divide-line rounded-md border border-line">
            {list.map((rule) => (
              <li key={rule.id} className="flex items-center gap-3 px-3.5 py-2.5">
                <span className="ident text-fg">{rule.cidr}</span>
                {rule.label && (
                  <span className="min-w-0 truncate text-xs text-fg-subtle">{rule.label}</span>
                )}
                <span className="ml-auto shrink-0 text-xs text-fg-subtle">
                  {relativeTime(rule.createdAt)}
                </span>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={removing === rule.id}
                    onClick={() => void remove(rule.id)}
                  >
                    {t("allowlist.remove")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          /* A form, so Enter in either field adds — the way anyone who has
             typed a CIDR into a firewall UI before will try to submit it. */
          <form
            className="grid gap-3 sm:grid-cols-[1.2fr_1fr_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <Field label={t("allowlist.cidr")} htmlFor="access-cidr">
              <Input
                id="access-cidr"
                autoComplete="off"
                placeholder="203.0.113.0/24"
                value={cidr}
                onChange={(e) => setCidr(e.target.value)}
              />
            </Field>
            <Field label={t("allowlist.label")} htmlFor="access-label">
              <Input
                id="access-label"
                autoComplete="off"
                placeholder={t("allowlist.labelPlaceholder")}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Field>
            <Button
              type="submit"
              variant="secondary"
              loading={addRule.isPending}
              disabled={cidr.trim() === ""}
            >
              Add
            </Button>
          </form>
        )}

        {/*
          Said where somebody is typing a network, because the consequence is
          invisible from the list above: the rules decide who may reach the
          port, so a list naming only IPv4 closes it to all of IPv6. Anybody
          whose client resolves to a v6 address is cut off by an allowlist that
          looks complete.
        */}
        {canManage && list.length > 0 && (
          <p className="text-xs text-fg-subtle">
            {t("allowlist.ipNote")}
          </p>
        )}

        {error && <Alert tone="error">{error}</Alert>}
      </div>
    </Card>
  );
}
